import {
  Drawer,
  DrawerContent,
  DrawerFooter,
} from '@finn/design-system/atoms/drawer';
import { useMediaQuery } from '@finn/design-system/helpers/media';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { useIntl } from '@finn/ui-utils';
import { useCallback, useContext } from 'react';

import { getClosedUserGroupId, getForBusinessCookie } from '../../../';
import { ContinueButton } from '../../configuration/components/ContinueButton';
import { PriceDisplay } from '../../configuration/components/PriceDisplay';
import { VehicleContext, VehicleProvider } from '../../product-cards';
import { useSubscriptionPrice } from '../../product-pricing';
import { EquipmentDetailsComponent } from './EquipmentDetailsComponent';
import { KeyData } from './KeyData';
import { VehicleHeader } from './VehicleHeader';

type Props = {
  open: boolean;
  onClose: () => void;
  productDetailsData: any;
  onAddToCart: () => void;
};

export const CarInformationDrawer = ({
  open,
  onClose,
  onAddToCart,
  productDetailsData,
}: Props) => {
  const i18n = useIntl();
  const isMDScreen = useMediaQuery('md');

  const handleModalClose = useCallback(() => {
    interactionTrackingEvent(TrackingEventName.MODAL_CLOSED, {
      name: 'carinfo',
    });
    onClose();
  }, [onClose]);

  const context = useContext(VehicleContext);
  const { vehicle: pdpVehicle, infoDrawerVehicle } = context;
  const vehicle = infoDrawerVehicle || pdpVehicle;

  const isForBusiness = getForBusinessCookie();
  const closedUserGroupId = getClosedUserGroupId();

  const { originalPriceFormatted, finalPriceFormatted } = useSubscriptionPrice({
    vehicle,
    closedUserGroupId,
  });

  const subtitle = i18n.formatMessage(
    `pdp.price.${isForBusiness ? 'b2b' : 'b2c'}_vat`
  );

  const isShortPreDiscountPrice =
    originalPriceFormatted?.toString().length === 4;

  return (
    <Drawer
      open={open}
      onClose={handleModalClose}
      handleOnly
      direction={isMDScreen ? 'bottom' : 'right'}
    >
      <DrawerContent
        title={i18n.formatMessage('pdp.carInfo')}
        className="!select-text overflow-visible"
      >
        {/* This extra Provider allows us to overwrite the vehicle in the context, without affecting the PDP */}
        <VehicleProvider {...context} vehicle={vehicle}>
          <VehicleHeader vehicle={vehicle} />
          <KeyData />
          <hr className="w-full border-0 border-b border-solid border-pearl" />
          <EquipmentDetailsComponent productDetailsData={productDetailsData} />
          <div className="pb-16" />
        </VehicleProvider>
        <DrawerFooter className="z-overlay">
          <PriceDisplay
            subtitle={subtitle}
            finalPrice={finalPriceFormatted}
            originalPrice={originalPriceFormatted}
            isShortPrice={isShortPreDiscountPrice}
            hasOriginalPrice={Boolean(originalPriceFormatted)}
          />

          <div className="w-1/2">
            <ContinueButton
              isInPDP
              testId="submit-config"
              location="info"
              onClick={() => {
                handleModalClose();
                onAddToCart();
              }}
            />
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
